import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Trash2, ShieldAlert, Eye, Loader2, Users, CreditCard } from 'lucide-react';
import {
  listAggregatorTeamMembers,
  removeAggregatorTeamMember,
  changeAggregatorTeamMemberRole,
  getAggregatorPortfolioProjects,
} from '../../services/api';
import { useAggregator } from '../../contexts/AggregatorContext';
import { InviteTeamMemberDialog } from '../../components/aggregator/InviteTeamMemberDialog';
import { TransferBillingOwnerDialog } from '../../components/aggregator/TransferBillingOwnerDialog';
import { AnalystVisibilityEditor } from '../../components/aggregator/AnalystVisibilityEditor';
import TeamCapacityCard from '../../components/aggregator/TeamCapacityCard';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import AdminGate from '../../components/organisation/AdminGate';
import { useAggregatorAccessGate } from '../../hooks/useAggregatorAccessGate';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import type { AggregatorTeamMember, AggregatorRole } from '../../types';

const ROLE_LABELS: Record<AggregatorRole, string> = {
  billing_owner: 'Billing Owner',
  admin: 'Admin',
  analyst: 'Analyst',
};

const ROLE_BADGE_CLASSES: Record<AggregatorRole, string> = {
  billing_owner: 'bg-purple-100 text-purple-800',
  admin: 'bg-blue-100 text-blue-800',
  analyst: 'bg-gray-100 text-gray-700',
};

// Status (Fig60) reads the real accepted_at stamp aggregator_team/handler.py
// writes at row-creation time. Exported for a pure, provider-free unit test.
//
// NOTE (done-report flag): _accept_invite() is the ONLY place that ever
// writes a team-members row, and it always stamps accepted_at immediately —
// _invite() itself only mails a signed token and never persists a row. So in
// today's backend every row this endpoint returns already has accepted_at,
// meaning "Invite pending" can never actually appear yet. This column is
// still wired against the real field (not fabricated) so it starts working
// the moment a "pending invite" row is ever persisted — see the done report
// for the follow-up recommendation (a queryable outstanding-invites record).
export function memberStatus(member: Pick<AggregatorTeamMember, 'accepted_at'>): { label: string; className: string } {
  return member.accepted_at
    ? { label: 'Active', className: 'bg-emerald-100 text-emerald-700' }
    : { label: 'Invite pending', className: 'bg-amber-100 text-amber-800' };
}

// Visible-segments count (Fig60): the wire field is excluded_project_count
// (a count of HIDDEN projects), so "visible" = real total − real excluded.
// Returns null when either half is unknown — an honest "Manage" fallback,
// never a fabricated number. Exported for a pure, provider-free unit test.
export function visibleProjectCount(
  member: Pick<AggregatorTeamMember, 'excluded_project_count'>,
  totalProjects: number | null,
): number | null {
  if (totalProjects == null || member.excluded_project_count == null) return null;
  return Math.max(0, totalProjects - member.excluded_project_count);
}

export function TeamManagement() {
  const { activeAggregatorId, memberships, aggregatorTier } = useAggregator();
  const accessGate = useAggregatorAccessGate('the team');
  const queryClient = useQueryClient();

  const [showTransfer, setShowTransfer] = useState(false);
  // Bug #5858 — /agg/team?invite=analyst comes from Figure 13's "Invite as
  // Analyst →" in the sponsor's access editor: preselect the role and focus
  // the email field so the sponsor lands mid-act, not on a blank page.
  const [searchParams] = useSearchParams();
  const inviteParam = searchParams.get('invite');
  const inviteRole = inviteParam === 'admin' ? 'admin' : inviteParam === 'analyst' ? 'analyst' : undefined;
  const [visibilityTarget, setVisibilityTarget] = useState<AggregatorTeamMember | null>(null);
  const [removeTarget, setRemoveTarget] = useState<AggregatorTeamMember | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['aggregator-team-members', activeAggregatorId],
    queryFn: listAggregatorTeamMembers,
    enabled: !!activeAggregatorId,
    staleTime: 30_000,
  });

  // Real total project count behind "Visible segments" — same query key the
  // rest of the aggregator screens share, so this costs no extra fetch when
  // the caller arrived here from Portfolio in the same session.
  const { data: portfolioData } = useQuery({
    queryKey: ['aggregator-portfolio', activeAggregatorId],
    queryFn: () => getAggregatorPortfolioProjects(activeAggregatorId),
    enabled: !!activeAggregatorId,
  });
  const totalProjects = portfolioData ? (portfolioData.total_count ?? portfolioData.count) : null;

  const members = data?.members ?? [];
  const activeMembers = members.filter(m => !m.revoked_at);

  // Gate actions on the caller's own role (from the API), not on whether the
  // roster happens to contain a billing_owner/admin row.
  const callerRole = data?.caller_role ?? null;
  const isBillingOwner = callerRole === 'billing_owner';
  const isAdmin = callerRole === 'admin' || isBillingOwner;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['aggregator-team-members', activeAggregatorId] });
  };

  // Honest removal (P7): the real mutation runs inside ConfirmDialog. On failure
  // it throws so the dialog stays open and shows the error — never a fake success.
  const handleRemove = async (member: AggregatorTeamMember) => {
    await removeAggregatorTeamMember(member.member_user_id);
    invalidate();
  };

  const handleRoleChange = async (member: AggregatorTeamMember, newRole: AggregatorRole) => {
    setActionLoading(member.member_user_id + newRole);
    setActionError(null);
    try {
      await changeAggregatorTeamMemberRole(member.member_user_id, newRole);
      invalidate();
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to change role.');
    } finally {
      setActionLoading(null);
    }
  };

  const aggName = data?.aggregator_name
    ?? memberships.find((m) => m.aggregator_id === activeAggregatorId)?.aggregator_name
    ?? 'Your Organisation';

  // Someone who is not on any aggregator's team has no roster to show. Say so
  // plainly instead of "No team members yet" with controls that cannot work.
  if (accessGate) return accessGate;

  // A failed load replaces the page body: a 403 is not a transient error, so it
  // must not offer a Retry that cannot help (AdminGate only offers Try again
  // for errors that are not 403/404).
  if (isError) {
    return <AdminGate area="the team" orgName={aggName} error={error} onRetry={() => { void refetch(); }} />;
  }

  return (
    <div>
      <PageHeader
        title="Team & Portfolio"
        description={`Manage the analysts, admins and billing owner who work across ${aggName}.`}
        helpKey="agg-team"
        status={
          !isLoading && !isError
            ? { label: `${activeMembers.length} member${activeMembers.length === 1 ? '' : 's'}`, tone: 'neutral' }
            : undefined
        }
        secondaryActions={
          isBillingOwner ? (
            <span className="flex items-center gap-2">
              <Link
                to="/agg/billing"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <CreditCard className="w-4 h-4" />
                Billing
              </Link>
              <button
                onClick={() => setShowTransfer(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <ShieldAlert className="w-4 h-4" />
                Transfer Ownership
              </button>
            </span>
          ) : undefined
        }
      />

      {/* Error banner */}
      {actionError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {actionError}
        </div>
      )}

      {/* Content */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-iflow-600 animate-spin" />
        </div>
      )}

      {!isLoading && !isError && (
        activeMembers.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No team members yet"
            description="Invite analysts and admins to collaborate across your portfolio. Billing stays with the owner."
          />
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="min-w-0">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="border-b border-gray-100 px-6 py-3">
                  <h2 className="text-sm font-semibold text-gray-800">
                    Members <span className="font-normal text-gray-500">· {activeMembers.length} {activeMembers.length === 1 ? 'member' : 'members'}</span>
                  </h2>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Member</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Visible segments</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                      <th className="px-6 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {activeMembers.map(member => {
                      const loading = actionLoading?.startsWith(member.member_user_id);
                      const status = memberStatus(member);
                      const visible = visibleProjectCount(member, totalProjects);
                      return (
                        <tr key={member.member_user_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            {/* Show the name only when one exists; the email identifies
                                the member otherwise. Avoids a stray "—" above every
                                row when no display name is stored (T6). */}
                            {member.member_name && (
                              <div className="font-medium text-gray-900">{member.member_name}</div>
                            )}
                            <div className={member.member_name ? 'text-xs text-gray-500' : 'font-medium text-gray-900'}>{member.member_email}</div>
                          </td>
                          <td className="px-6 py-4">
                            {isAdmin && member.role !== 'billing_owner' ? (
                              <select
                                value={member.role}
                                onChange={e => handleRoleChange(member, e.target.value as AggregatorRole)}
                                disabled={!!loading}
                                className="text-xs px-2 py-1 border border-gray-200 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-iflow-500"
                              >
                                <option value="analyst" className="text-gray-900">Analyst</option>
                                <option value="admin" className="text-gray-900">Admin</option>
                              </select>
                            ) : (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ROLE_BADGE_CLASSES[member.role]}`}>
                                {ROLE_LABELS[member.role]}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {member.role === 'analyst' ? (
                              <button
                                onClick={() => setVisibilityTarget(member)}
                                className="flex items-center gap-1 text-xs text-iflow-600 hover:text-iflow-800"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                {visible != null ? `${visible} of ${totalProjects} visible` : 'Manage'}
                              </button>
                            ) : (
                              <span className="text-xs text-gray-500">All segments</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.className}`}
                              data-testid={`member-status-${member.member_user_id}`}
                            >
                              {status.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            {isAdmin && member.role !== 'billing_owner' && (
                              <button
                                onClick={() => setRemoveTarget(member)}
                                disabled={!!loading}
                                className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-40"
                                aria-label={`Remove ${member.member_name ?? member.member_email}`}
                              >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>

              {/* Role legend (Fig60) — static, mirrors the three real roles the
                  backend enforces (aggregator_team/handler.py). */}
              <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-600" data-testid="role-legend">
                <span className="font-semibold text-gray-700">Role legend</span> — <strong>Billing owner:</strong>{' '}
                full access + subscription &amp; invoices. <strong>Admin:</strong> full portfolio + team management, no
                billing. <strong>Analyst:</strong> read/triage only, limited to assigned segments.
              </div>
            </div>

            {/* Right rail (Fig60): the always-visible invite panel (admins
                only) + the seat-usage card (everyone — it is built entirely
                from the real roster count already loaded above). */}
            <div className="space-y-4">
              {isAdmin && (
                <InviteTeamMemberDialog variant="inline" onClose={() => {}} onSuccess={invalidate} initialRole={inviteRole} autoFocusEmail={!!inviteRole} />
              )}
              <TeamCapacityCard
                activeMembers={activeMembers.length}
                account={isBillingOwner ? aggregatorTier : null}
              />
            </div>
          </div>
        )
      )}

      {/* Dialogs */}
      {showTransfer && (
        <TransferBillingOwnerDialog
          members={activeMembers}
          onClose={() => setShowTransfer(false)}
          onSuccess={invalidate}
        />
      )}

      {visibilityTarget && (
        <AnalystVisibilityEditor
          analyst={visibilityTarget}
          onClose={() => setVisibilityTarget(null)}
          onSuccess={invalidate}
        />
      )}

      <ConfirmDialog
        open={!!removeTarget}
        title="Remove team member"
        tone="danger"
        confirmLabel="Remove member"
        message={
          removeTarget
            ? `Remove ${removeTarget.member_name ?? removeTarget.member_email} from the team? They lose access to your portfolio immediately.`
            : ''
        }
        onConfirm={async () => { if (removeTarget) await handleRemove(removeTarget); }}
        onClose={() => setRemoveTarget(null)}
      />
    </div>
  );
}
